import React, { useState, useEffect, useRef } from 'react';
import { dateFr, lireDateSaisie } from '../services/dates';
import './ChampDate.css';

/**
 * Champ de saisie d'une date, toujours en `jj/mm/aaaa` (lot « Dates », 2026-10-07).
 *
 * Remplace `<input type="date">`, dont l'affichage dépend du réglage du POSTE
 * (Windows en « Français (Canada) » → `aaaa-mm-jj`, anglais américain → `mm/jj/aaaa`).
 * Même contrat que lui, pour qu'un remplacement ne touche que la balise :
 *   value     `aaaa-mm-jj` ou ''
 *   onChange  reçoit `{ target: { value } }` — `aaaa-mm-jj`, ou '' tant que la saisie
 *             n'est pas une date complète et réelle (le parent ne garde jamais une
 *             ancienne date pendant qu'on en tape une autre)
 *   id, className, disabled, required, aria-label, min, max : comme un input
 *   style     posé sur le conteneur (largeur, flex)
 *
 * Le bouton calendrier ouvre le sélecteur du navigateur (`showPicker`), qui ne sert
 * qu'à choisir : l'affichage reste celui de ce champ.
 */
export default function ChampDate({ value, onChange, style, className, disabled, min, max, ...autres }) {
    const [texte, setTexte] = useState(() => dateFr(value));
    const [invalide, setInvalide] = useState(false);
    const dernierEmis = useRef(value || '');
    const selecteur = useRef(null);

    // Valeur changée par le parent (chargement, réinitialisation) : on la réaffiche.
    // Pas quand c'est l'écho de ce que l'on vient d'émettre : on effacerait la saisie en cours.
    useEffect(() => {
        if ((value || '') !== dernierEmis.current) {
            dernierEmis.current = value || '';
            setTexte(dateFr(value));
            setInvalide(false);
        }
    }, [value]);

    const emettre = (iso) => {
        dernierEmis.current = iso;
        if (onChange && iso !== (value || '')) onChange({ target: { value: iso } });
    };

    const saisir = (e) => {
        const t = e.target.value;
        setTexte(t);
        const iso = lireDateSaisie(t);
        if (iso) setInvalide(false);
        emettre(iso || '');
    };

    const quitter = () => {
        if (!texte.trim()) { setInvalide(false); return; }
        const iso = lireDateSaisie(texte);
        if (iso) setTexte(dateFr(iso));
        setInvalide(!iso);
    };

    const ouvrirCalendrier = () => {
        const el = selecteur.current;
        if (!el || disabled) return;
        try { el.showPicker(); } catch { el.focus(); }
    };

    const choisi = (e) => {
        const iso = e.target.value;
        setTexte(dateFr(iso));
        setInvalide(false);
        emettre(iso);
    };

    return (
        <span className="champ-date" style={style}>
            <input
                {...autres}
                type="text"
                inputMode="numeric"
                autoComplete="off"
                placeholder="jj/mm/aaaa"
                maxLength={10}
                className={`${className || ''}${invalide ? ' champ-date-invalide' : ''}`.trim() || undefined}
                disabled={disabled}
                value={texte}
                onChange={saisir}
                onBlur={quitter}
                aria-invalid={invalide || undefined}
                title={invalide ? "Date impossible : saisissez jj/mm/aaaa (ex. 07/10/2026)" : undefined}
            />
            <button type="button" className="champ-date-bouton" onClick={ouvrirCalendrier}
                    disabled={disabled} tabIndex={-1} aria-label="Choisir dans le calendrier">
                <i className="fa-regular fa-calendar" />
            </button>
            <input ref={selecteur} type="date" className="champ-date-selecteur" tabIndex={-1} aria-hidden="true"
                   value={lireDateSaisie(texte) || ''} min={min} max={max} onChange={choisi} />
        </span>
    );
}
